import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute, GlobalAdminRoute } from "@/components/app/guards";
import AppLayout from "@/components/app/AppLayout";
import { DefaultSeo } from "@/components/marketing/DefaultSeo";

import Home from "@/pages/marketing/Home";
import NotFound from "@/pages/marketing/NotFound";
import Pricing from "@/pages/marketing/Pricing";
import DemoDashboard from "@/pages/marketing/DemoDashboard";
import Integrations from "@/pages/marketing/Integrations";
import Contact from "@/pages/marketing/Contact";
import Privacy from "@/pages/marketing/Privacy";
import Terms from "@/pages/marketing/Terms";
import About from "@/pages/marketing/About";
import Press from "@/pages/marketing/Press";
import Affiliates from "@/pages/marketing/Affiliates";

import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AuthCallback from "@/pages/auth/AuthCallback";
import MsAuthCallback from "@/pages/auth/MsAuthCallback";

import ResourcesIndex, { RESOURCE_SECTIONS } from "@/pages/resources/ResourcesIndex";
import ResourcePage from "@/pages/resources/ResourcePage";
import { useParams } from "react-router-dom";

function ResourcePathRouter() {
  const { section } = useParams();
  return RESOURCE_SECTIONS.has(section) ? <ResourcesIndex /> : <ResourcePage />;
}

const Onboarding = lazy(() => import("@/pages/app/Onboarding"));
const Dashboard = lazy(() => import("@/pages/app/Dashboard"));
const DecisionCentre = lazy(() => import("@/pages/app/DecisionCentre"));
const Forecasts = lazy(() => import("@/pages/app/Forecasts"));
const Scenarios = lazy(() => import("@/pages/app/Scenarios"));
const ScenarioBuilder = lazy(() => import("@/pages/app/ScenarioBuilder"));
const Reports = lazy(() => import("@/pages/app/Reports"));
const PnLSummary = lazy(() => import("@/pages/app/PnLSummary"));
const StaffCosts = lazy(() => import("@/pages/app/StaffCosts"));
const Settings = lazy(() => import("@/pages/app/Settings"));
const Billing = lazy(() => import("@/pages/app/Billing"));
const IntegrationHub = lazy(() => import("@/pages/app/IntegrationHub"));
const IdentityAccess = lazy(() => import("@/pages/app/IdentityAccess"));
const TimeTracker = lazy(() => import("@/pages/app/TimeTracker"));
const ValueAnalytics = lazy(() => import("@/pages/app/ValueAnalytics"));
const AiTools = lazy(() => import("@/pages/app/AiTools"));
const EvidenceRegister = lazy(() => import("@/pages/app/EvidenceRegister"));
const Admin = lazy(() => import("@/pages/app/Admin"));

const CmsLayout = lazy(() => import("@/pages/cms/CmsLayout"));
const PseoLayout = lazy(() => import("@/pages/pseo/PseoLayout"));
const PseoOverview = lazy(() => import("@/pages/pseo/PseoOverview"));
const PseoInventory = lazy(() => import("@/pages/pseo/PseoInventory"));
const PseoSources = lazy(() => import("@/pages/pseo/PseoSources"));
const PseoSignals = lazy(() => import("@/pages/pseo/PseoSignals"));
const PseoOpportunities = lazy(() => import("@/pages/pseo/PseoOpportunities"));
const PseoChanges = lazy(() => import("@/pages/pseo/PseoChanges"));
const PseoDigests = lazy(() => import("@/pages/pseo/PseoDigests"));
const PseoRuns = lazy(() => import("@/pages/pseo/PseoRuns"));
const SeoDashboard = lazy(() => import("@/pages/cms/SeoDashboard"));
const ContentPages = lazy(() => import("@/pages/cms/ContentPages"));
const ContentPageEditor = lazy(() => import("@/pages/cms/ContentPageEditor"));
const ContentImports = lazy(() => import("@/pages/cms/ContentImports"));
const TopicClusters = lazy(() => import("@/pages/cms/TopicClusters"));
const SeoSilos = lazy(() => import("@/pages/cms/SeoSilos"));
const CtaLibrary = lazy(() => import("@/pages/cms/CtaLibrary"));
const ProofBlocks = lazy(() => import("@/pages/cms/ProofBlocks"));
const InternalLinks = lazy(() => import("@/pages/cms/InternalLinks"));
const Redirects = lazy(() => import("@/pages/cms/Redirects"));
const Performance = lazy(() => import("@/pages/cms/Performance"));
const VisualAssets = lazy(() => import("@/pages/cms/VisualAssets"));

function captureUtm() {
  try {
    const params = new URLSearchParams(window.location.search);
    const fields = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
    const existing = JSON.parse(sessionStorage.getItem("aidelta_utm") || "{}");
    let has = false;
    const utm = { ...existing };
    fields.forEach((f) => { if (params.get(f)) { utm[f] = params.get(f); has = true; } });
    if (has || !existing.landing_page) {
      utm.landing_page = utm.landing_page || window.location.pathname;
      utm.referrer = utm.referrer || document.referrer;
      sessionStorage.setItem("aidelta_utm", JSON.stringify(utm));
    }
  } catch (e) { console.debug("UTM capture skipped:", e?.message || e); }
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent Google OAuth callback before normal routing (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Microsoft Entra redirect response (MSAL SPA returns #code=...&state=...)
  if (location.hash?.includes("code=") && location.hash?.includes("state=")) {
    return <MsAuthCallback />;
  }
  // Pages that render their own <SeoHead> must not also get the site default,
  // or crawlers see duplicate title/description/canonical tags.
  const isResourceRoute =
    location.pathname.startsWith("/resources") ||
    ["/about", "/press", "/affiliates"].includes(location.pathname.replace(/\/$/, "") || "/");
  return (
    <>
      {!isResourceRoute ? <DefaultSeo pathname={location.pathname} /> : null}
      <Suspense fallback={<div className="flex min-h-[40vh] items-center justify-center text-sm text-slate-400">Loading…</div>}>
      <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/demo" element={<DemoDashboard />} />
      <Route path="/integrations" element={<Integrations />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/about" element={<About />} />
      <Route path="/press" element={<Press />} />
      <Route path="/affiliates" element={<Affiliates />} />

      <Route path="/resources" element={<ResourcesIndex />} />
      <Route path="/resources/:section" element={<ResourcePathRouter />} />
      <Route path="/resources/*" element={<ResourcePage />} />

      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/app" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="value" element={<ValueAnalytics />} />
        <Route path="ai-tools" element={<AiTools />} />
        <Route path="evidence" element={<EvidenceRegister />} />
        <Route path="decision-centre" element={<DecisionCentre />} />
        <Route path="forecasts" element={<Forecasts />} />
        <Route path="scenarios" element={<Scenarios />} />
        <Route path="scenarios/new" element={<ScenarioBuilder />} />
        <Route path="scenarios/:id" element={<ScenarioBuilder />} />
        <Route path="time-tracker" element={<TimeTracker />} />
        <Route path="entries" element={<Navigate to="/app/time-tracker" replace />} />
        <Route path="entries/*" element={<Navigate to="/app/time-tracker" replace />} />
        <Route path="reports" element={<Reports />} />
        <Route path="pnl" element={<PnLSummary />} />
        <Route path="staff-costs" element={<StaffCosts />} />
        <Route path="integrations" element={<IntegrationHub />} />
        <Route path="identity" element={<IdentityAccess />} />
        <Route path="settings" element={<Settings />} />
        <Route path="billing" element={<Billing />} />
      </Route>

      <Route path="/admin" element={<GlobalAdminRoute><Admin /></GlobalAdminRoute>} />
      <Route path="/admin/pseo" element={<GlobalAdminRoute><PseoLayout /></GlobalAdminRoute>}>
        <Route index element={<PseoOverview />} />
        <Route path="inventory" element={<PseoInventory />} />
        <Route path="sources" element={<PseoSources />} />
        <Route path="signals" element={<PseoSignals />} />
        <Route path="opportunities" element={<PseoOpportunities />} />
        <Route path="changes" element={<PseoChanges />} />
        <Route path="digests" element={<PseoDigests />} />
        <Route path="runs" element={<PseoRuns />} />
      </Route>
      <Route path="/admin/seo" element={<GlobalAdminRoute><CmsLayout /></GlobalAdminRoute>}>
        <Route index element={<SeoDashboard />} />
        <Route path="pages" element={<ContentPages />} />
        <Route path="pages/new" element={<ContentPageEditor />} />
        <Route path="pages/:id" element={<ContentPageEditor />} />
        <Route path="imports" element={<ContentImports />} />
        <Route path="assets" element={<VisualAssets />} />
        <Route path="silos" element={<SeoSilos />} />
        <Route path="clusters" element={<TopicClusters />} />
        <Route path="ctas" element={<CtaLibrary />} />
        <Route path="proof-blocks" element={<ProofBlocks />} />
        <Route path="internal-links" element={<InternalLinks />} />
        <Route path="redirects" element={<Redirects />} />
        <Route path="performance" element={<Performance />} />
      </Route>
      <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </>
  );
}

function App() {
  useEffect(() => { captureUtm(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
